// src/hooks/useFetchMenu.js
import { useState, useEffect } from 'react';

// Función para normalizar flags de productos (soporta múltiples formatos de API)
const normalizeProductFlags = (product) => {
  return {
    ...product,
    isRecommended: product.isRecommended || product.recommended || product.is_recommended || false,
    isMostOrdered: product.isMostOrdered || product.mostOrdered || product.is_most_ordered || product.most_ordered || false,
    isSignature: product.isSignature || product.signature || product.is_signature || false,
    isShareable: product.isShareable || product.shareable || product.is_shareable || false,
    isProductOfDay: product.isProductOfDay || product.productOfDay || product.is_product_of_day || product.product_of_day || false,
    pairingSuggestion: product.pairingSuggestion || product.pairing_suggestion || product.suggestion || null,
    acidity: product.acidity ?? product.acidez ?? null,
    intensity: product.intensity ?? product.intensidad ?? null,
    aromas: product.aromas ?? null,
    funkiness: product.funkiness ?? product.funk ?? null,
    wineType: product.wineType || product.wine_type || product.color || product.colour || null,
  };
};

// Función para ordenar productos con estrategia de anclaje de precio y premium first
const sortProducts = (products) => {
  return [...products].sort((a, b) => {
    const priceA = a.price || 0;
    const priceB = b.price || 0;
    
    // Prioridad 1: Producto del día
    if (a.isProductOfDay && !b.isProductOfDay) return -1;
    if (!a.isProductOfDay && b.isProductOfDay) return 1;
    
    // Prioridad 2: Signature
    if (a.isSignature && !b.isSignature) return -1;
    if (!a.isSignature && b.isSignature) return 1;
    
    // Prioridad 3: Recomendados
    if (a.isRecommended && !b.isRecommended) return -1;
    if (!a.isRecommended && b.isRecommended) return 1;
    
    // Prioridad 4: Más pedidos
    if (a.isMostOrdered && !b.isMostOrdered) return -1;
    if (!a.isMostOrdered && b.isMostOrdered) return 1;
    
    // Prioridad 5: Anclaje de precio (productos premium primero, luego orden descendente)
    // Esto crea un efecto de anclaje donde los precios altos se ven primero
    const isPremiumA = priceA > 1500; // > 15 EUR
    const isPremiumB = priceB > 1500;
    
    if (isPremiumA && !isPremiumB) return -1;
    if (!isPremiumA && isPremiumB) return 1;
    
    // Si ambos son premium o ambos no, ordenar por precio descendente
    return priceB - priceA;
  });
};

// Función para procesar categorías y sus productos
const processCategories = (categories) => {
  if (!categories || !Array.isArray(categories)) return [];
  
  return categories.map(category => {
    if (!category.products || !Array.isArray(category.products)) {
      return { ...category, products: [] };
    }
    
    // Normalizar flags de productos
    const normalizedProducts = category.products.map(normalizeProductFlags);
    
    // Ordenar productos con estrategia inteligente
    const sortedProducts = sortProducts(normalizedProducts);
    
    // Agregar categoryId a cada producto para navegación
    const productsWithCategory = sortedProducts.map(product => ({
      ...product,
      categoryId: category.id
    }));
    
    return {
      ...category,
      products: productsWithCategory
    };
  });
};

// Función para extraer todos los productos destacados de todas las categorías
const extractFeaturedProducts = (categories) => {
  const allProducts = [];
  categories.forEach(category => {
    if (category.products && Array.isArray(category.products)) {
      category.products.forEach(product => {
        if (
          product.isRecommended ||
          product.isMostOrdered ||
          product.isSignature ||
          product.isProductOfDay
        ) {
          allProducts.push({
            ...product,
            categoryId: category.id,
            categoryName: category.name
          });
        }
      });
    }
  });
  
  // Ordenar destacados: producto del día primero, luego signature, luego recomendados
  return allProducts.sort((a, b) => {
    if (a.isProductOfDay && !b.isProductOfDay) return -1;
    if (!a.isProductOfDay && b.isProductOfDay) return 1;
    if (a.isSignature && !b.isSignature) return -1;
    if (!a.isSignature && b.isSignature) return 1;
    return 0;
  });
};

// ID especial para la categoría de destacados
export const FEATURED_CATEGORY_ID = 9999999;

// Función para crear categoría especial de productos destacados
const createFeaturedCategory = (categories) => {
  const allFeaturedProducts = [];
  
  categories.forEach(category => {
    if (category.products && Array.isArray(category.products)) {
      category.products.forEach(product => {
        const hasBadge = product.isRecommended || 
                        product.isMostOrdered || 
                        product.isSignature || 
                        product.isShareable || 
                        product.isProductOfDay;
        const hasSuggestion = product.pairingSuggestion;
        
        // Incluir productos con badges o sugerencias de maridaje
        if (hasBadge || hasSuggestion) {
          allFeaturedProducts.push({
            ...product,
            categoryId: category.id,
            categoryName: category.name
          });
        }
      });
    }
  });
  
  // Ordenar usando la misma lógica que sortProducts
  const sortedProducts = sortProducts(allFeaturedProducts);
  
  return {
    id: FEATURED_CATEGORY_ID,
    name: 'Destacados',
    products: sortedProducts,
    icon: null
  };
};

const useFetchMenu = (url, refreshKey = 0) => {
  const [menuItems, setMenuItems] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [allFeaturedProducts, setAllFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Error al obtener los datos');
        }
        const data = await response.json();
        
        // Procesar categorías con normalización y ordenamiento
        const processedCategories = processCategories(data.categories || []);
        
        // Extraer productos destacados de todas las categorías
        const featuredProducts = extractFeaturedProducts(processedCategories);
        
        // Crear categoría especial de destacados
        const featuredCategory = createFeaturedCategory(processedCategories);
        
        // Agregar la categoría de destacados al principio de la lista
        const categoriesWithFeatured = [featuredCategory, ...processedCategories];
        
        setMenuItems(categoriesWithFeatured);
        setRecommendedProducts(data.recommendedProducts || []);
        setAllFeaturedProducts(featuredProducts);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [url, refreshKey]);

  return { 
    menuItems, 
    recommendedProducts, 
    allFeaturedProducts,
    loading, 
    error 
  };
};

export default useFetchMenu;
